import React, { useContext, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../authProvider.mjs';
import PageShell from './PageShell.mjs';

/**
 * Gate for routes that need a COMPLETE profile (region + position).
 *
 * Profile completeness was only ever enforced at the OAuth callback. A user who
 * navigated away from /complete-profile stayed signed in without a position and
 * could open claim forms that then misbehaved - the claim carries the traveller's
 * position, and reports are grouped by region.
 *
 * This sends them to /complete-profile instead, remembering where they were
 * headed so they land back there once the profile is filled in.
 */
const ProfileRequired = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const incomplete = !!user && (!user.region || !user.position);

  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) {
      navigate('/login', { replace: true, state: { returnUrl: location.pathname } });
      return;
    }
    if (incomplete) {
      navigate('/complete-profile', {
        replace: true,
        state: { returnUrl: location.pathname },
      });
    }
  }, [isAuthenticated, isLoading, incomplete, navigate, location.pathname]);

  if (isLoading) {
    return (
      <PageShell>
        <div className="bg-white rounded-xl shadow-lg py-16 flex flex-col items-center justify-center">
          <div className="w-10 h-10 border-4 border-gray-200 border-t-stanford-red rounded-full animate-spin mb-4"></div>
          <p className="text-gray-600">Loading…</p>
        </div>
      </PageShell>
    );
  }

  if (!isAuthenticated || incomplete) return null;

  return children;
};

export default ProfileRequired;
